import { useCallback, useEffect, useState } from "react";

import { isCancelLinkToken, type PublicCancellation } from "../domain/types";
import { PublicAppointmentsAPI } from "../infrastructure/api/PublicAppointments.api";

export type PublicCancellationState =
    | { kind: "loading" }
    | { kind: "notFound" }
    | { kind: "error" }
    | { kind: "ready"; cancellation: PublicCancellation };

const httpStatus = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } })?.response?.status;

const refusalDetail = (error: unknown): string | undefined => {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail;
    return typeof detail === "string" ? detail : undefined;
};

/** The token a cancel link carries, from the fragment where the email put it. */
const tokenFromFragment = (): string => window.location.hash.replace(/^#/, "");

/**
 * Everything the public cancel page does, apart from drawing it.
 *
 * The token is read once, then removed from the address bar: it is a bearer
 * credential, and a URL is copied, bookmarked, synced and shown in history.
 * A value that is not shaped like a token we issue is answered as "not found"
 * without asking the server.
 */
export function usePublicCancellation() {
    // Read in the initialiser, removed in an effect: an initialiser may run
    // twice, and the second run must still see the fragment.
    const [token] = useState(tokenFromFragment);
    const validShape = isCancelLinkToken(token);

    const [state, setState] = useState<PublicCancellationState>(
        validShape ? { kind: "loading" } : { kind: "notFound" }
    );
    const [submitting, setSubmitting] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    useEffect(() => {
        if (!window.location.hash) return;
        window.history.replaceState(
            window.history.state,
            "",
            window.location.pathname + window.location.search
        );
    }, []);

    const load = useCallback(() => {
        if (!validShape) {
            setState({ kind: "notFound" });
            return;
        }
        setState({ kind: "loading" });
        PublicAppointmentsAPI.lookup(token)
            .then(({ data }) => setState({ kind: "ready", cancellation: data }))
            .catch((error) =>
                setState({ kind: httpStatus(error) === 404 ? "notFound" : "error" })
            );
    }, [token, validShape]);

    useEffect(() => {
        load();
    }, [load]);

    const confirm = useCallback(async () => {
        setSubmitting(true);
        setRefusal(null);
        try {
            const { data } = await PublicAppointmentsAPI.cancel(token);
            setState({ kind: "ready", cancellation: data });
        } catch (error) {
            const status = httpStatus(error);
            const detail = refusalDetail(error);
            if (status === 404) {
                setState({ kind: "notFound" });
            } else if (status === 409 && detail) {
                setRefusal(`appointments.cancel.refused.${detail}`);
            } else {
                setRefusal("appointments.cancel.error");
            }
        } finally {
            setSubmitting(false);
        }
    }, [token]);

    return { state, submitting, refusal, retry: load, confirm };
}
